// Flatten raw GraphQL attachment nodes into clean AttachmentItem objects
// so the UI doesn't have to deal with nested fragments/extensions.

import type { AttachmentItem, AttachmentUser } from '../attachmentsMacroTypes.ts';

import type { OperationCheck } from './extractPermissions.ts';
import { extractPermissions } from './extractPermissions.ts';
import type { ExtensionKeyValue } from './getExtensionValue.ts';
import { getExtensionValue } from './getExtensionValue.ts';

// Raw Person fragment — fields are optional depending on which fragment matched.
type RawUser = {
	readonly accountId?: string | null;
	readonly displayName?: string | null;
	readonly profilePicture?: { readonly path: string } | null;
} | null;

// Declared manually instead of derived from the generated query type because both
// the main query and version-history query share this normalizer, and the generated
// types have deeply nested nullable paths that cause cascading TS errors.
export type RawAttachmentNode = {
	readonly id: string | null;
	readonly title: string | null;
	readonly type: string | null;
	readonly extensions: ReadonlyArray<ExtensionKeyValue> | null;
	readonly version: {
		readonly number: number | null;
		readonly when: string | null;
		readonly message: string | null;
		readonly by: RawUser;
	} | null;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- false positive: GraphQL field name
	readonly history: {
		readonly createdDate: string | null;
		readonly createdBy: RawUser;
	} | null;
	readonly labels: {
		readonly nodes: ReadonlyArray<{
			readonly id: string | null;
			readonly name: string | null;
		} | null> | null;
	} | null;
	readonly operations: ReadonlyArray<OperationCheck> | null;
	readonly links: { readonly download: string | null } | null;
} | null;

const UNKNOWN_USER: AttachmentUser = {
	displayName: '',
	accountId: '',
	profilePicture: { path: '' },
};

// Convert raw Person → AttachmentUser. Returns empty user for anonymous.
const normalizeUser = (raw: RawUser): AttachmentUser => {
	if (!raw) return UNKNOWN_USER;
	return {
		displayName: raw.displayName ?? '',
		accountId: raw.accountId ?? '',
		profilePicture: { path: raw.profilePicture?.path ?? '' },
	};
};

// Strip `?version=N` for a stable "latest version" download path.
const stripVersionParam = (downloadPath: string): string => {
	const idx = downloadPath.indexOf('?version=');
	return idx >= 0 ? downloadPath.slice(0, idx) : downloadPath;
};

// Returns null for nodes missing id/title so callers can filter them out.
export const normalizeAttachment = (raw: RawAttachmentNode): AttachmentItem | null => {
	if (!raw || !raw.id || !raw.title) {
		return null;
	}

	const fileSize = Number(getExtensionValue(raw.extensions, 'fileSize') ?? 0);
	const mediaType = getExtensionValue(raw.extensions, 'mediaType') ?? '';
	const fileId = getExtensionValue(raw.extensions, 'fileId') ?? '';

	const downloadPath = raw.links?.download ?? '';
	const downloadPathWithoutVersion = stripVersionParam(downloadPath);

	const lastModificationDate = raw.version?.when ?? '';
	const lastModifier = normalizeUser(raw.version?.by ?? null);
	const creationDate = raw.history?.createdDate ?? '';
	const creator = normalizeUser(raw.history?.createdBy ?? null);

	const labels: string[] =
		raw.labels?.nodes?.flatMap((label) => (label?.name ? [label.name] : [])) ?? [];

	const permissions = extractPermissions(raw.operations);

	return {
		id: raw.id,
		fileId,
		fileName: raw.title,
		mediaType,
		fileSize,
		downloadPath,
		downloadPathWithoutVersion,
		lastModificationDate,
		lastModifier,
		creationDate,
		creator,
		versionComment: raw.version?.message ?? '',
		version: raw.version?.number ?? 1,
		labels,
		...permissions,
	};
};
